import React from 'react';
import { Typography } from 'antd';

const { Title, Paragraph } = Typography;

export const TermsContentES: React.FC = () => {
  return (
    <div className="terms-content">
      <Paragraph className="terms-intro">
        Estos Términos y Condiciones de Uso (los "Términos") rigen el acceso y uso de la plataforma DivvyStash 
        (el "Servicio"). El Servicio es operado por DivvyStash, una entidad de Georgia, Estados Unidos ("DivvyStash," 
        "nosotros," "nuestro").
      </Paragraph>

      <Paragraph className="terms-intro">
        Al acceder o usar el Servicio, usted ("usted," "Usuario") acepta estar sujeto a estos Términos. Si no está de 
        acuerdo con estos Términos, no debe acceder ni usar el Servicio.
      </Paragraph>

      <Title level={2} className="section-title">1. Descripción General del Servicio</Title>

      <Title level={3} className="subsection-title">1.1 Descripción</Title>
      <Paragraph>
        DivvyStash es una plataforma en línea solo por invitación que permite a un administrador ("Admin") rastrear 
        contribuciones de capital agrupadas, asignaciones de ganancias, porcentajes de reinversión y solicitudes de retiro 
        entre un grupo cerrado de participantes ("Contribuyentes" o "Usuarios"). El Servicio proporciona solo mantenimiento 
        de registros y análisis; no ejecuta operaciones, no retiene fondos de clientes, ni actúa como corredor, agente, 
        asesor de inversiones o custodio.
      </Paragraph>

      <Title level={3} className="subsection-title">1.2 Sin Corretaje ni Custodia</Title>
      <Paragraph>
        Toda la actividad de inversión subyacente y el movimiento de dinero ocurren fuera del Servicio y son gestionados 
        directamente por el Admin o partes relacionadas. DivvyStash no acepta depósitos, no procesa retiros de dinero o 
        activos criptográficos, y no controla ninguna cuenta bancaria, de corretaje o billetera del Usuario.
      </Paragraph>

      <Title level={3} className="subsection-title">1.3 Relación Entre Usuarios</Title>
      <Paragraph>
        La relación económica (incluido cualquier préstamo, participación en ganancias o acuerdo de inversión) entre el 
        Admin y los Usuarios es independiente del Servicio y es un acuerdo privado entre esas partes. DivvyStash no es 
        parte de esos acuerdos.
      </Paragraph>

      <Title level={2} className="section-title">2. Elegibilidad y Cuentas</Title>

      <Title level={3} className="subsection-title">2.1 Elegibilidad</Title>
      <Paragraph>Para usar el Servicio, usted debe:</Paragraph>
      <ul className="terms-list">
        <li>Tener al menos dieciocho (18) años de edad (o la mayoría de edad en su jurisdicción, si es mayor); y</li>
        <li>Haber sido invitado al Servicio por un Admin o por DivvyStash en nombre de un Admin.</li>
      </ul>

      <Title level={3} className="subsection-title">2.2 Acceso Solo por Invitación</Title>
      <Paragraph>
        Los usuarios no pueden auto-registrarse. La creación de cuentas solo está disponible a través de un enlace de 
        invitación único o código enviado a la dirección de correo electrónico designada por el Admin.
      </Paragraph>

      <Title level={3} className="subsection-title">2.3 Información de la Cuenta</Title>
      <Paragraph>
        Usted acepta proporcionar información precisa, actual y completa durante el registro de la cuenta y mantener esa 
        información actualizada, incluyendo pero no limitado a:
      </Paragraph>
      <ul className="terms-list">
        <li>Nombre legal</li>
        <li>Dirección de correo electrónico</li>
        <li>País de residencia</li>
        <li>Cualquier otra información razonablemente solicitada para fines de cumplimiento o impuestos</li>
      </ul>

      <Title level={3} className="subsection-title">2.4 Seguridad de la Cuenta</Title>
      <Paragraph>
        Usted es responsable de mantener la confidencialidad de sus credenciales de inicio de sesión y de toda actividad 
        que ocurra bajo su cuenta. Usted acepta notificarnos de inmediato de cualquier uso no autorizado o sospecha de 
        compromiso.
      </Paragraph>

      <Title level={2} className="section-title">3. Roles: Admin, Contribuyentes y DivvyStash</Title>

      <Title level={3} className="subsection-title">3.1 Admin</Title>
      <Paragraph>El Admin es la parte (o partes) que:</Paragraph>
      <ul className="terms-list">
        <li>Aporta un fondo inicial de capital (su principal base);</li>
        <li>Invita a los Usuarios a participar;</li>
        <li>Establece contribuciones mínimas requeridas;</li>
        <li>Registra montos de ganancias en el Servicio para asignación entre Usuarios; y</li>
        <li>Procesa solicitudes de retiro fuera de la plataforma.</li>
      </ul>

      <Title level={3} className="subsection-title">3.2 Contribuyentes / Usuarios</Title>
      <Paragraph>Los Contribuyentes son Usuarios invitados que:</Paragraph>
      <ul className="terms-list">
        <li>Proporcionan un monto de contribución inicial, sujeto a un mínimo establecido por el Admin;</li>
        <li>Tienen un principal base registrado en el Servicio;</li>
        <li>Reciben una parte de las ganancias registradas proporcional a su contribución; y</li>
        <li>Pueden elegir un porcentaje de reinversión y enviar solicitudes de retiro a través del Servicio.</li>
      </ul>

      <Title level={3} className="subsection-title">3.3 Rol de DivvyStash</Title>
      <Paragraph><strong>DivvyStash proporciona:</strong></Paragraph>
      <ul className="terms-list">
        <li>Las herramientas de software para registrar contribuciones, calcular y mostrar asignaciones de ganancias, 
            y rastrear saldos de reinversión/retirables;</li>
        <li>Un panel de usuario e interfaz de administrador;</li>
        <li>Cierta funcionalidad de informes y resúmenes (por ejemplo, semana hasta la fecha, mes hasta la fecha, 
            año hasta la fecha y ganancias totales).</li>
      </ul>

      <Paragraph><strong>DivvyStash no:</strong></Paragraph>
      <ul className="terms-list">
        <li>Realiza o ejecuta operaciones o decisiones de inversión;</li>
        <li>Garantiza o verifica la precisión de las cifras de ganancias ingresadas por el Admin;</li>
        <li>Garantiza el momento o la finalización de los retiros;</li>
        <li>Proporciona asesoramiento de inversión, legal o fiscal individualizado.</li>
      </ul>

      <Title level={2} className="section-title">4. Contribuciones, Principal Base y Elegibilidad para Ganancias</Title>

      <Title level={3} className="subsection-title">4.1 Contribución Mínima</Title>
      <Paragraph>
        Para cada Usuario invitado, el Admin puede establecer una contribución mínima requerida. El Usuario no será elegible 
        para recibir asignaciones de ganancias hasta que este mínimo haya sido satisfecho y confirmado en el Servicio.
      </Paragraph>

      <Title level={3} className="subsection-title">4.2 Principal Base</Title>
      <Paragraph>Una vez confirmada la contribución inicial de un Usuario:</Paragraph>
      <ul className="terms-list">
        <li>El principal base del Usuario se registra en el Servicio.</li>
        <li>El principal base se trata como bloqueado para propósitos de calcular porcentajes de participación.</li>
        <li>Entre las partes, los términos económicos de la contribución (por ejemplo, préstamo vs. participación en 
            ganancias) son determinados por el Admin y el Usuario fuera del Servicio.</li>
      </ul>

      <Title level={3} className="subsection-title">4.3 Sin Depósitos Continuos (Reglas MVP)</Title>
      <Paragraph>
        A menos que y hasta que el Servicio admita explícitamente contribuciones adicionales, no se puede agregar dinero 
        nuevo por un Usuario después de que su contribución inicial esté bloqueada. Cualquier cambio a esta regla debe 
        reflejarse en Términos actualizados y/o divulgaciones separadas.
      </Paragraph>

      <Title level={3} className="subsection-title">4.4 Principal Base del Admin</Title>
      <Paragraph>
        El Admin también puede tener un principal base. El Servicio puede ajustar el principal base registrado del Admin 
        cuando nuevos Usuarios contribuyan, sujeto a cualquier base mínima que el Admin elija mantener.
      </Paragraph>

      <Title level={2} className="section-title">5. Asignación de Ganancias y Tarifas de la Empresa</Title>

      <Title level={3} className="subsection-title">5.1 Entradas de Ganancias</Title>
      <Paragraph>Cuando el Admin registra un monto de ganancia (por ejemplo, una ganancia diaria o periódica):</Paragraph>
      <ul className="terms-list">
        <li>El Servicio calcula la participación de cada Usuario basándose en la proporción del Usuario del pool total 
            (estructura de principal base).</li>
        <li>El Servicio luego registra la ganancia neta para cada Usuario después de aplicar cualquier tarifa aplicable 
            de DivvyStash y/o reglas específicas del Admin.</li>
      </ul>

      <Title level={3} className="subsection-title">5.2 Tarifas de la Empresa</Title>
      <Paragraph>
        DivvyStash cobra una tarifa fija sobre las ganancias del Usuario, estructurada como un porcentaje de las 
        ganancias asignadas a cada Usuario. La estructura de tarifas es la siguiente:
      </Paragraph>
      <ul className="terms-list">
        <li>Para Usuarios ubicados en los Estados Unidos: una tarifa fija del 20% sobre las ganancias asignadas;</li>
        <li>Para Usuarios ubicados fuera de los Estados Unidos: una tarifa fija del 20% sobre las ganancias asignadas.</li>
      </ul>
      <Paragraph>
        Su tarifa aplicable está determinada por su designación de país al crear la cuenta y no cambia con el tiempo. 
        Todas las tarifas, una vez evaluadas, reducen la ganancia neta acreditada a usted. La empresa puede subsidiar 
        las tasas de tarifas efectivas manteniendo capital de la empresa invertido en el fondo junto con los fondos de los usuarios.
      </Paragraph>

      <Title level={3} className="subsection-title">5.3 Sin Garantía de Ganancias</Title>
      <Paragraph>
        Los montos de ganancias registrados en el Servicio representan cifras ingresadas por el Admin. DivvyStash no 
        audita ni verifica independientemente dichos montos y no es responsable de ninguna discrepancia entre las 
        ganancias registradas y los resultados de inversión subyacentes.
      </Paragraph>

      <Title level={2} className="section-title">6. Reinversión y Montos Retirables</Title>

      <Title level={3} className="subsection-title">6.1 Porcentaje de Reinversión</Title>
      <Paragraph>
        El Servicio permite a cada Usuario establecer un porcentaje de reinversión (por ejemplo, 0–100%) que determina 
        cuánto de su ganancia neta semanal se agregará a su principal base.
      </Paragraph>

      <Title level={3} className="subsection-title">6.2 Liquidaciones Semanales</Title>
      <Paragraph>
        En un horario semanal definido (por ejemplo, sábado a las 23:59 UTC) o en intervalos determinados por el Admin:
      </Paragraph>
      <ul className="terms-list">
        <li>El Servicio calcula la ganancia del Usuario para la semana (neta de tarifas de la empresa).</li>
        <li>Una porción igual al porcentaje de reinversión del Usuario se agrega al principal base del Usuario.</li>
        <li>El resto se mueve a un saldo de monto retirable.</li>
        <li>El contador de "ganancia de la semana" se reinicia en el Servicio.</li>
      </ul>

      <Title level={3} className="subsection-title">6.3 Efecto de la Reinversión</Title>
      <Paragraph>
        Los montos reinvertidos aumentan el principal base del Usuario para fines de cálculos de asignación futura. La 
        reinversión no cambia la línea base de contribución histórica para fines fiscales o contables; esas relaciones se 
        definen fuera del Servicio.
      </Paragraph>

      <Title level={2} className="section-title">7. Retiros</Title>

      <Title level={3} className="subsection-title">7.1 Solicitudes de Retiro</Title>
      <Paragraph>
        Los Usuarios pueden solicitar retiros de su saldo retirable a través del Servicio. Una solicitud de retiro es una 
        señal al Admin y no mueve ningún fondo por sí misma.
      </Paragraph>

      <Title level={3} className="subsection-title">7.2 Procesamiento del Admin</Title>
      <Paragraph>El Admin es el único responsable de:</Paragraph>
      <ul className="terms-list">
        <li>Revisar y aprobar, rechazar o marcar solicitudes como completadas; y</li>
        <li>Iniciar y completar el pago real (por ejemplo, transferencia bancaria, transferencia de billetera) fuera del Servicio.</li>
      </ul>

      <Title level={3} className="subsection-title">7.3 Tiempo y Retrasos</Title>
      <Paragraph>DivvyStash no controla, y no es responsable de, ningún retraso o falla en:</Paragraph>
      <ul className="terms-list">
        <li>Procesamiento de pagos por bancos, proveedores de pago o redes blockchain;</li>
        <li>La capacidad de respuesta o acciones del propio Admin.</li>
      </ul>

      <Title level={3} className="subsection-title">7.4 Sin Custodia</Title>
      <Paragraph>
        DivvyStash no retiene ni controla fondos de Usuarios y no puede emitir ni revertir pagos en su nombre.
      </Paragraph>

      <Title level={2} className="section-title">8. Riesgo de Inversión; Sin Asesoramiento</Title>

      <Title level={3} className="subsection-title">8.1 Riesgo de Pérdida</Title>
      <Paragraph>Usted reconoce y acepta que:</Paragraph>
      <ul className="terms-list">
        <li>Todas las inversiones implican riesgo, incluida la posible pérdida de parte o todo el capital que ha contribuido;</li>
        <li>No hay garantía de ganancia, retorno o protección del principal; y</li>
        <li>El rendimiento pasado de cualquier estrategia de inversión o Admin no garantiza resultados futuros.</li>
      </ul>

      <Title level={3} className="subsection-title">8.2 Sin Asesoramiento de Inversión</Title>
      <Paragraph>
        El Servicio y cualquier contenido proporcionado a través de él son solo para fines informativos y administrativos. 
        Nada en o a través del Servicio constituye:
      </Paragraph>
      <ul className="terms-list">
        <li>Asesoramiento de inversión;</li>
        <li>Una recomendación para comprar, vender o mantener cualquier valor, activo digital u otro instrumento; o</li>
        <li>Asesoramiento legal, fiscal o contable.</li>
      </ul>

      <Title level={3} className="subsection-title">8.3 Decisiones Independientes</Title>
      <Paragraph>
        Usted es el único responsable de cualquier decisión que tome con respecto a la participación, contribuciones, 
        retiros o confianza en la información de rendimiento mostrada en el Servicio. Debe consultar a sus propios 
        asesores financieros, legales y fiscales.
      </Paragraph>

      <Title level={2} className="section-title">9. Impuestos y Retención</Title>

      <Title level={3} className="subsection-title">9.1 Responsabilidad del Usuario</Title>
      <Paragraph>Usted es el único responsable de:</Paragraph>
      <ul className="terms-list">
        <li>Determinar sus propias obligaciones fiscales que surjan de la participación en cualquier acuerdo rastreado por el Servicio; y</li>
        <li>Presentar todas las declaraciones de impuestos requeridas y pagar cualquier impuesto aplicable en su jurisdicción.</li>
      </ul>

      <Title level={3} className="subsection-title">9.2 Sin Asesoramiento Fiscal</Title>
      <Paragraph>
        DivvyStash no proporciona asesoramiento fiscal ni retiene impuestos de las distribuciones. Cualquier resumen financiero 
        o etiqueta en el Servicio es solo informativo y no constituye asesoramiento fiscal.
      </Paragraph>

      <Title level={2} className="section-title">10. Obligaciones del Usuario y Conducta Prohibida</Title>

      <Title level={3} className="subsection-title">10.1 Sus Obligaciones</Title>
      <Paragraph>Usted acepta:</Paragraph>
      <ul className="terms-list">
        <li>Usar el Servicio solo de acuerdo con estos Términos y la ley aplicable;</li>
        <li>Proporcionar información precisa y veraz y mantenerla actualizada;</li>
        <li>Mantener la confidencialidad de sus credenciales de cuenta;</li>
        <li>Notificarnos de inmediato sobre cualquier acceso no autorizado o incidente de seguridad que involucre su cuenta.</li>
      </ul>

      <Title level={3} className="subsection-title">10.2 Usos Prohibidos</Title>
      <Paragraph>Usted acepta no:</Paragraph>
      <ul className="terms-list">
        <li>Usar el Servicio para cualquier propósito ilegal, incluido el fraude, el lavado de dinero o el financiamiento del terrorismo;</li>
        <li>Hacerse pasar por cualquier persona o entidad, o tergiversar su afiliación;</li>
        <li>Interferir con o interrumpir el funcionamiento del Servicio, incluso mediante la introducción de virus, malware 
            u otro código dañino;</li>
        <li>Intentar obtener acceso no autorizado a otras cuentas o datos;</li>
        <li>Usar cualquier medio automatizado (como bots o scrapers) para acceder o recopilar información del Servicio, 
            excepto según lo expresamente permitido;</li>
        <li>Eludir o intentar eludir cualquier medida de seguridad o control de acceso.</li>
      </ul>
      <Paragraph>
        DivvyStash se reserva el derecho de investigar y, cuando sea apropiado, informar cualquier uso indebido sospechoso 
        a las autoridades policiales u otras autoridades.
      </Paragraph>

      <Title level={2} className="section-title">11. Privacidad y Datos</Title>

      <Title level={3} className="subsection-title">11.1 Política de Privacidad</Title>
      <Paragraph>
        Su uso del Servicio también se rige por nuestra Política de Privacidad, que describe cómo recopilamos, usamos y 
        compartimos información sobre usted. La Política de Privacidad se incorpora a estos Términos por referencia.
      </Paragraph>

      <Title level={3} className="subsection-title">11.2 Datos Recopilados</Title>
      <Paragraph>Sin limitar la Política de Privacidad, el Servicio puede recopilar y almacenar:</Paragraph>
      <ul className="terms-list">
        <li>Información de cuenta y perfil;</li>
        <li>Registros de contribución y asignación de ganancias;</li>
        <li>Solicitudes de retiro y estado;</li>
        <li>Datos técnicos y de uso (incluidos registros, datos del dispositivo y ubicación aproximada).</li>
      </ul>

      <Title level={3} className="subsection-title">11.3 Seguridad</Title>
      <Paragraph>
        Implementamos medidas técnicas y organizativas razonables diseñadas para proteger su información. Sin embargo, 
        ningún sistema es completamente seguro, y no podemos garantizar que terceros no autorizados nunca derroten 
        nuestras medidas de seguridad.
      </Paragraph>

      <Title level={2} className="section-title">12. Propiedad Intelectual</Title>

      <Title level={3} className="subsection-title">12.1 Propiedad</Title>
      <Paragraph>DivvyStash y sus licenciantes poseen todos los derechos, títulos e intereses en y para el Servicio, incluidos:</Paragraph>
      <ul className="terms-list">
        <li>El nombre y logotipo de DivvyStash;</li>
        <li>Todo el software, código, texto, gráficos y otro contenido; y</li>
        <li>Todos los derechos de propiedad intelectual asociados.</li>
      </ul>

      <Title level={3} className="subsection-title">12.2 Licencia Limitada</Title>
      <Paragraph>
        Sujeto a su cumplimiento de estos Términos, le otorgamos una licencia limitada, no exclusiva, intransferible y 
        revocable para acceder y usar el Servicio para sus propósitos personales o comerciales internos.
      </Paragraph>

      <Title level={3} className="subsection-title">12.3 Restricciones</Title>
      <Paragraph>No puede:</Paragraph>
      <ul className="terms-list">
        <li>Copiar, modificar, distribuir, vender o arrendar ninguna parte del Servicio;</li>
        <li>Realizar ingeniería inversa o intentar extraer el código fuente, excepto en la medida en que dichas 
            restricciones estén prohibidas por la ley;</li>
        <li>Eliminar, ocultar o alterar cualquier aviso de propiedad.</li>
      </ul>

      <Title level={2} className="section-title">13. Disponibilidad, Cambios y Actualizaciones del Servicio</Title>

      <Title level={3} className="subsection-title">13.1 Sin Garantía de Disponibilidad</Title>
      <Paragraph>
        No garantizamos que el Servicio esté disponible en todo momento o sin interrupción. El mantenimiento, problemas 
        técnicos u otros eventos pueden resultar en tiempo de inactividad.
      </Paragraph>

      <Title level={3} className="subsection-title">13.2 Cambios al Servicio</Title>
      <Paragraph>
        Podemos modificar, mejorar o discontinuar cualquier parte del Servicio en cualquier momento, con o sin aviso, 
        siempre que dichos cambios no violen materialmente estos Términos.
      </Paragraph>

      <Title level={3} className="subsection-title">13.3 Características Beta</Title>
      <Paragraph>
        Podemos ofrecer características nuevas o experimentales designadas como "beta" o similares. Dichas características 
        pueden modificarse o discontinuarse a nuestra discreción y se proporcionan "tal cual" sin garantías.
      </Paragraph>

      <Title level={2} className="section-title">14. Suspensión y Terminación</Title>

      <Title level={3} className="subsection-title">14.1 Suspensión o Terminación por DivvyStash</Title>
      <Paragraph>Podemos suspender o terminar su acceso al Servicio, en su totalidad o en parte, si:</Paragraph>
      <ul className="terms-list">
        <li>Usted viola estos Términos;</li>
        <li>Sospechamos actividad fraudulenta o ilegal;</li>
        <li>Estamos obligados a hacerlo por ley o solicitud regulatoria; o</li>
        <li>Continuar proporcionándole el Servicio crearía un riesgo indebido para nosotros.</li>
      </ul>

      <Title level={3} className="subsection-title">14.2 Efecto de la Terminación</Title>
      <Paragraph>Tras la terminación:</Paragraph>
      <ul className="terms-list">
        <li>Su derecho a acceder al Servicio cesará;</li>
        <li>Podemos retener registros según lo requiera la ley o para fines comerciales legítimos;</li>
        <li>Cualquier derecho o obligación económica entre usted y el Admin (por ejemplo, saldos pendientes) permanecen 
            entre usted y el Admin y no se extinguen por su pérdida de acceso al Servicio.</li>
      </ul>

      <Title level={3} className="subsection-title">14.3 Cierre Voluntario</Title>
      <Paragraph>
        Puede solicitar el cierre de su cuenta en cualquier momento contactándonos. Podemos retener ciertos datos para 
        cumplir con obligaciones legales o para el mantenimiento legítimo de registros.
      </Paragraph>

      <Title level={2} className="section-title">15. Descargos de Responsabilidad</Title>

      <Title level={3} className="subsection-title">15.1 "Tal Cual" y "Según Disponibilidad"</Title>
      <Paragraph>
        El Servicio se proporciona "tal cual" y "según disponibilidad" sin garantías de ningún tipo, ya sean expresas, 
        implícitas o estatutarias, incluidas las garantías implícitas de comerciabilidad, idoneidad para un propósito 
        particular, título y no infracción.
      </Paragraph>

      <Title level={3} className="subsection-title">15.2 Precisión de la Información</Title>
      <Paragraph>
        Si bien nos esforzamos por proporcionar información precisa y actualizada, no garantizamos que los datos, análisis 
        o resúmenes disponibles a través del Servicio sean precisos, completos o libres de errores. Las entradas de 
        ganancias y muchos otros puntos de datos son proporcionados por el Admin y pueden no ser verificados de forma 
        independiente.
      </Paragraph>

      <Title level={3} className="subsection-title">15.3 Servicios de Terceros</Title>
      <Paragraph>
        El Servicio puede depender de proveedores de terceros (por ejemplo, alojamiento, correo electrónico, análisis). 
        No somos responsables de los actos, omisiones o disponibilidad de dichos terceros.
      </Paragraph>

      <Title level={2} className="section-title">16. Limitación de Responsabilidad</Title>

      <Paragraph>En la máxima medida permitida por la ley:</Paragraph>

      <Title level={3} className="subsection-title">16.1 Sin Daños Indirectos</Title>
      <Paragraph>
        DivvyStash y sus afiliados, funcionarios, directores, empleados y agentes no serán responsables de ningún daño 
        indirecto, incidental, consecuente, especial, punitivo o ejemplar, incluidas las ganancias perdidas, pérdida de 
        datos o interrupción del negocio, que surjan de o estén relacionados con su uso o incapacidad para usar el Servicio.
      </Paragraph>

      <Title level={3} className="subsection-title">16.2 Límite de Responsabilidad</Title>
      <Paragraph>
        La responsabilidad agregada total de DivvyStash que surja de o esté relacionada con estos Términos o su uso del 
        Servicio no excederá el mayor de:
      </Paragraph>
      <ul className="terms-list">
        <li>El monto total de tarifas, si las hay, pagadas por usted a DivvyStash por el uso del Servicio durante los 
            doce (12) meses anteriores al evento que da lugar al reclamo; o</li>
        <li>USD $1,000.</li>
      </ul>

      <Title level={3} className="subsection-title">16.3 Aplicabilidad</Title>
      <Paragraph>
        Algunas jurisdicciones no permiten la exclusión o limitación de ciertos daños, por lo que las limitaciones 
        anteriores pueden no aplicarse a usted. En tales casos, nuestra responsabilidad se limitará en la máxima medida 
        permitida por la ley.
      </Paragraph>

      <Title level={2} className="section-title">17. Indemnización</Title>

      <Paragraph>
        Usted acepta indemnizar, defender y eximir de responsabilidad a DivvyStash y sus afiliados, funcionarios, 
        directores, empleados y agentes de y contra todos y cada uno de los reclamos, responsabilidades, daños, pérdidas 
        y gastos (incluidos los honorarios razonables de abogados) que surjan de o estén relacionados con:
      </Paragraph>
      <ul className="terms-list">
        <li>Su uso del Servicio;</li>
        <li>Su violación de estos Términos;</li>
        <li>Su violación de los derechos de otra persona o entidad; o</li>
        <li>Cualquier disputa entre usted y el Admin o cualquier otro Usuario con respecto a contribuciones, ganancias o retiros.</li>
      </ul>

      <Title level={2} className="section-title">18. Ley Aplicable y Resolución de Disputas</Title>

      <Title level={3} className="subsection-title">18.1 Ley Aplicable</Title>
      <Paragraph>
        Estos Términos y cualquier disputa que surja de o esté relacionada con ellos o el Servicio se regirán e 
        interpretarán de acuerdo con las leyes del Estado de Georgia, sin tener en cuenta sus principios de conflicto 
        de leyes.
      </Paragraph>

      <Title level={3} className="subsection-title">18.2 Resolución de Disputas; Arbitraje</Title>
      <Paragraph>
        Cualquier disputa, reclamo o controversia que surja de o esté relacionada con estos Términos o el Servicio que 
        no pueda resolverse de manera informal será finalmente resuelta mediante arbitraje vinculante administrado por 
        la Asociación Americana de Arbitraje (AAA) bajo sus reglas aplicables. El arbitraje se llevará a cabo en 
        Suwanee, Georgia en el idioma inglés, y la decisión del árbitro será final y vinculante.
      </Paragraph>
      <Paragraph>
        Usted y DivvyStash acuerdan resolver disputas de forma individual, y no como parte de ninguna acción colectiva 
        o representativa, en la máxima medida permitida por la ley.
      </Paragraph>

      <Title level={2} className="section-title">19. Cambios a Estos Términos</Title>

      <Paragraph>
        Podemos actualizar estos Términos de vez en cuando. Cuando lo hagamos, revisaremos la fecha de "Última actualización" 
        en la parte superior de este documento y podemos proporcionar un aviso adicional (como por correo electrónico o 
        notificación en la aplicación) cuando lo requiera la ley.
      </Paragraph>

      <Paragraph>
        Si continúa usando el Servicio después de que los Términos actualizados entren en vigencia, se considerará que ha 
        aceptado los cambios. Si no está de acuerdo con los Términos actualizados, debe dejar de usar el Servicio.
      </Paragraph>

      <Title level={2} className="section-title">20. Misceláneos</Title>

      <Title level={3} className="subsection-title">20.1 Acuerdo Completo</Title>
      <Paragraph>
        Estos Términos, junto con cualquier política o término adicional referido aquí (incluida cualquier Política de 
        Privacidad aplicable), constituyen el acuerdo completo entre usted y DivvyStash con respecto al Servicio.
      </Paragraph>

      <Title level={3} className="subsection-title">20.2 Divisibilidad</Title>
      <Paragraph>
        Si alguna disposición de estos Términos se considera inválida o inaplicable, las disposiciones restantes 
        permanecerán en pleno vigor y efecto.
      </Paragraph>

      <Title level={3} className="subsection-title">20.3 No Renuncia</Title>
      <Paragraph>
        Nuestro incumplimiento de hacer cumplir cualquier derecho o disposición de estos Términos no constituirá una 
        renuncia a dicho derecho o disposición.
      </Paragraph>

      <Title level={3} className="subsection-title">20.4 Cesión</Title>
      <Paragraph>
        No puede ceder ni transferir sus derechos u obligaciones bajo estos Términos sin nuestro consentimiento previo 
        por escrito. Podemos ceder estos Términos sin restricción.
      </Paragraph>

      <Title level={3} className="subsection-title">20.5 Contacto</Title>
      <Paragraph>
        Si tiene alguna pregunta sobre estos Términos o el Servicio, puede contactarnos en:
      </Paragraph>
      <div className="contact-info">
        <Paragraph><strong>Nombre de la Empresa:</strong> DivvyStash</Paragraph>
        <Paragraph><strong>Correo Electrónico:</strong> support@divvystash.com</Paragraph>
        <Paragraph><strong>Dirección:</strong> 2320 Prosperity Way, Suwanee, GA 30024</Paragraph>
      </div>
    </div>
  );
};
